import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import api from '../services/api';
import type { Member as ApiMember } from '../types';
import type { LiffProfile } from '@misterlex223/cloudflare-line-liff-sdk/react';

export type MemberRole = 'member' | 'moderator' | 'admin' | 'super_admin';

// Use Member type from types/index.ts but extend for auth context compatibility
interface Member extends ApiMember {
  displayName?: string; // Alias for name (deprecated)
  companyName?: string; // Alias for storeName (deprecated)
}

interface AuthContextType {
  user: Member | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean; // Whether AuthContext is currently initializing (fetching user)
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithLine: (token: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (data: Partial<Member>) => Promise<{ success: boolean; error?: string }>;
  hasRole: (roles: MemberRole | MemberRole[]) => boolean;
  hasPermission: (permission: string) => boolean;

  // LINE LIFF Central Service Integration (v1.0.0)
  lineProfile?: LiffProfile;
  isLineLinked: boolean;
  linkLineAccount: (code: string) => Promise<{ success: boolean; error?: string }>;
  unlinkLineAccount: () => Promise<{ success: boolean; error?: string }>;
  refreshLineToken: () => Promise<{ success: boolean; error?: string }>;

  // Email Binding for LINE Users (v0.9.1+)
  bindEmail: (email: string) => Promise<{ success: boolean; error?: string }>;

  // Password Reset
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string, code: string, newPassword: string, confirmPassword: string) => Promise<{ success: boolean; error?: string }>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  storeName?: string;
  serviceAreas?: string[];
  serviceTypes?: string[];
}

interface LoginResponse {
  member: Member;
  token: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* eslint-disable react-refresh/only-export-components */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false); // Track if initialization is in progress

  // LINE LIFF State
  const [lineProfile, setLineProfile] = useState<LiffProfile | undefined>();
  const [isLineLinked, setIsLineLinked] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchCurrentUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      setIsInitializing(true);
      const token = localStorage.getItem('auth_token');
      console.log('[AuthContext] fetchCurrentUser called, token exists:', !!token);
      const response = await api.get<Member>('/api/members/me');
      console.log('[AuthContext] fetchCurrentUser response:', response);
      if (response.success && response.data) {
        console.log('[AuthContext] Setting user:', response.data);
        setUser(response.data);
      } else {
        console.warn('[AuthContext] Failed to fetch user:', response.error);
        // Token invalid, clear it
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch current user:', error);
      localStorage.removeItem('auth_token');
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post<LoginResponse>('/api/members/login', {
        email,
        password,
      });

      if (response.success && response.data) {
        const { member, token } = response.data;
        localStorage.setItem('auth_token', token);
        setUser(member);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Login failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await api.post<LoginResponse>('/api/members/register', data);

      if (response.success && response.data) {
        const { member, token } = response.data;
        localStorage.setItem('auth_token', token);
        setUser(member);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Registration failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      };
    }
  };

  const loginWithLine = async (token: string) => {
    try {
      // Token is already provided by LINE login flow
      // Store it and fetch user info
      localStorage.setItem('auth_token', token);

      const response = await api.get<Member>('/api/members/me');
      if (response.success && response.data) {
        setUser(response.data);
        return { success: true };
      }

      // If fetching user fails, clear the token
      localStorage.removeItem('auth_token');
      return {
        success: false,
        error: response.error?.message || 'LINE login failed',
      };
    } catch (error) {
      localStorage.removeItem('auth_token');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'LINE login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  const updateUser = async (data: Partial<Member>) => {
    try {
      const response = await api.patch<Member>('/api/members/me', data);

      if (response.success && response.data) {
        setUser(response.data);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Update failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Update failed',
      };
    }
  };

  /**
   * Check if current user has one of the specified roles
   */
  const hasRole = (roles: MemberRole | MemberRole[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  /**
   * Check if current user has a specific permission
   * Super admins have all permissions
   */
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;

    // Define role permissions
    // Member (一般會員): Can post wishes, manage own profile
    // Moderator (版主): Can review ads/wishes, access queue
    // Admin (管理員): All moderator permissions + member management
    // Super Admin (超級管理員): Full system access
    const rolePermissions: Record<MemberRole, string[]> = {
      member: ['read:own_ads', 'read:own_wishes', 'create:own_ads', 'create:own_wishes'],
      moderator: [
        'read:own_ads', 'read:own_wishes', 'create:own_ads', 'create:own_wishes',
        'approve:ads', 'approve:wishes', 'reject:ads', 'reject:wishes',
        'read:audit_logs', 'read:queue'
      ],
      admin: [
        'read:own_ads', 'read:own_wishes', 'create:own_ads', 'create:own_wishes',
        'approve:ads', 'approve:wishes', 'reject:ads', 'reject:wishes',
        'read:audit_logs', 'read:queue', 'manage:members'
      ],
      super_admin: ['*']
    };

    const permissions = rolePermissions[user.role] || [];
    return permissions.includes('*') || permissions.includes(permission);
  };

  // LINE Account Linking Methods
  const linkLineAccount = async (code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ member: Member; lineProfile: LiffProfile }>('/api/members/line/link', { code });

      if (response.success && response.data) {
        setLineProfile(response.data.lineProfile);
        setIsLineLinked(true);
        // Update user with LINE data
        if (response.data.member) {
          setUser(response.data.member);
        }
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'LINE account linking failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'LINE account linking failed',
      };
    }
  };

  const unlinkLineAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ member: Member }>('/api/members/line/unlink', {});

      if (response.success && response.data) {
        setLineProfile(undefined);
        setIsLineLinked(false);
        setUser(response.data.member);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'LINE account unlinking failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'LINE account unlinking failed',
      };
    }
  };

  const refreshLineToken = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ lineProfile: LiffProfile }>('/api/members/line/refresh-token', {});

      if (response.success && response.data) {
        setLineProfile(response.data.lineProfile);
        setIsLineLinked(true);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'LINE token refresh failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'LINE token refresh failed',
      };
    }
  };

  const bindEmail = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ member: Member }>('/api/members/me/bind-email', { email });

      if (response.success && response.data) {
        setUser(response.data.member);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Email binding failed',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Email binding failed',
      };
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ message: string }>('/api/members/forgot-password', { email });

      if (response.success) {
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Failed to send password reset email',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send password reset email',
      };
    }
  };

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post<{ message: string }>('/api/members/reset-password', {
        email,
        code,
        newPassword,
        confirmPassword,
      });

      if (response.success) {
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Failed to reset password',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reset password',
      };
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isInitializing,
    login,
    loginWithLine,
    register,
    logout,
    updateUser,
    hasRole,
    hasPermission,
    // LINE LIFF Central Service Integration
    lineProfile,
    isLineLinked,
    linkLineAccount,
    unlinkLineAccount,
    refreshLineToken,
    // Email Binding
    bindEmail,
    // Password Reset
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
